import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { connectSocket, getSocket, joinRegionalChat, sendRegionalChatMessage, leaveRegionalChat } from '../socketClient';

const REGIONS = [
  'Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast',
  'Pacific Northwest', 'Mid-Atlantic', 'New England', 'Great Lakes',
  'Great Plains', 'Rocky Mountain', 'South'
];

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export default function RegionalChatPage() {
  const { user, token } = useAuth();
  const isMobile = useIsMobile();
  const [selectedRegion, setSelectedRegion] = useState(user?.region || 'Midwest');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const currentRegionRef = useRef(null);

  // Connect socket and join region
  useEffect(() => {
    if (!token) return;

    connectSocket(token);

    return () => {
      if (currentRegionRef.current) {
        leaveRegionalChat(currentRegionRef.current);
      }
    };
  }, [token]);

  // Join selected region
  useEffect(() => {
    if (!token || !selectedRegion) return;

    const join = async () => {
      // Leave previous region
      if (currentRegionRef.current && currentRegionRef.current !== selectedRegion) {
        leaveRegionalChat(currentRegionRef.current);
      }

      const result = await joinRegionalChat(selectedRegion);
      if (result.success) {
        setMessages(result.history || []);
        setConnected(true);
        currentRegionRef.current = selectedRegion;
      } else {
        setError(result.error || 'Failed to join chat');
      }
    };

    join();
  }, [token, selectedRegion]);

  // Listen for new messages
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onMessage = (msg) => {
      setMessages(prev => [...prev, msg]);
    };

    const onError = (data) => {
      setError(data.error);
      setTimeout(() => setError(''), 3000);
    };

    socket.on('regional-chat-message', onMessage);
    socket.on('regional-chat-error', onError);

    return () => {
      socket.off('regional-chat-message', onMessage);
      socket.off('regional-chat-error', onError);
    };
  }, [connected]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    sendRegionalChatMessage(selectedRegion, input.trim());
    setInput('');
  };

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 32px',
      maxWidth: 700, margin: '0 auto'
    }}>
      <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: '#c9d1d9', marginBottom: 16 }}>
        Regional Chat
      </h1>

      {/* Region selector */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 6,
        marginBottom: 16
      }}>
        {REGIONS.map(region => (
          <button
            key={region}
            onClick={() => setSelectedRegion(region)}
            style={{
              padding: '6px 12px', borderRadius: 16,
              border: selectedRegion === region ? '2px solid #d4af37' : '1px solid #30363d',
              background: selectedRegion === region ? '#2d1f12' : '#161b22',
              color: selectedRegion === region ? '#d4af37' : '#8b949e',
              fontSize: 12, fontWeight: 600, cursor: 'pointer'
            }}
          >
            {region}
          </button>
        ))}
      </div>

      {/* Chat area */}
      <div style={{
        background: '#0d1117', border: '1px solid #30363d', borderRadius: 10,
        height: isMobile ? 400 : 500, display: 'flex', flexDirection: 'column'
      }}>
        {/* Messages */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: 12,
          display: 'flex', flexDirection: 'column', gap: 6
        }}>
          {messages.length === 0 && (
            <div style={{ color: '#484f58', textAlign: 'center', padding: 40, fontSize: 14 }}>
              No messages yet. Be the first to say hello in {selectedRegion}!
            </div>
          )}
          {messages.map((msg, i) => {
            const isOwn = msg.userId === user?.userId;
            return (
              <div key={msg.messageId || i} style={{
                alignSelf: isOwn ? 'flex-end' : 'flex-start',
                maxWidth: '75%'
              }}>
                {!isOwn && (
                  <div style={{ fontSize: 10, color: '#8b949e', marginBottom: 2, fontWeight: 600 }}>
                    {msg.nickname}
                  </div>
                )}
                <div style={{
                  background: isOwn ? '#1a3a1a' : '#161b22',
                  border: `1px solid ${isOwn ? '#3fb950' : '#30363d'}`,
                  color: '#c9d1d9', padding: '8px 12px',
                  borderRadius: 8, fontSize: 13, wordBreak: 'break-word'
                }}>
                  {msg.message}
                </div>
                <div style={{ fontSize: 9, color: '#484f58', marginTop: 2, textAlign: isOwn ? 'right' : 'left' }}>
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#3d1f1f', color: '#f85149', padding: '6px 12px',
            fontSize: 12, textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSend} style={{
          display: 'flex', gap: 8, padding: 10,
          borderTop: '1px solid #30363d'
        }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Chat in ${selectedRegion}...`}
            maxLength={500}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 6,
              background: '#161b22', border: '1px solid #30363d',
              color: '#c9d1d9', fontSize: 13, outline: 'none'
            }}
          />
          <button type="submit" style={{
            padding: '8px 16px', borderRadius: 6,
            background: '#d4af37', color: '#0d1117', border: 'none',
            fontWeight: 600, fontSize: 13, cursor: 'pointer'
          }}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
